import { Order, MagnetDesign } from '../types';

export const NFC_SURCHARGE_PER_MAGNET = 1.50;

export interface OrderItemPricing {
  baseUnitPrice: number;
  nfcSurcharge: number;
  unitPrice: number;
  lineTotal: number;
}

/**
 * Resolves the exact individual unit price and line total for a magnet line item in an order,
 * ensuring NFC surcharges (+$1.50/ea) and premade price overrides stay strictly intact on the
 * specific item and are NEVER averaged across other items in the cart/order.
 */
export function getOrderItemPricing(order: Order, targetMag: MagnetDesign): OrderItemPricing {
  const qty = Math.max(1, Number(targetMag.quantity) || 1);
  const nfcSurcharge = targetMag.nfcEnabled ? NFC_SURCHARGE_PER_MAGNET : 0;

  // 1. If the order item already has its exact unitPrice persisted from checkout, use it directly
  if (typeof targetMag.unitPrice === 'number' && !Number.isNaN(targetMag.unitPrice) && targetMag.unitPrice > 0) {
    const unitPrice = Number(targetMag.unitPrice.toFixed(2));
    const baseUnitPrice =
      typeof targetMag.baseUnitPrice === 'number' && !Number.isNaN(targetMag.baseUnitPrice)
        ? Number(targetMag.baseUnitPrice.toFixed(2))
        : Number(Math.max(0, unitPrice - nfcSurcharge).toFixed(2));
    const lineTotal =
      typeof targetMag.lineTotal === 'number' && !Number.isNaN(targetMag.lineTotal)
        ? Number(targetMag.lineTotal.toFixed(2))
        : Number((unitPrice * qty).toFixed(2));
    return { baseUnitPrice, nfcSurcharge, unitPrice, lineTotal };
  }

  // 2. For premade items with an explicit priceOverride
  if (targetMag.priceOverride !== undefined && targetMag.priceOverride !== null && Number(targetMag.priceOverride) > 0) {
    const baseUnitPrice = Number(Number(targetMag.priceOverride).toFixed(2));
    const unitPrice = Number((baseUnitPrice + nfcSurcharge).toFixed(2));
    const lineTotal = Number((unitPrice * qty).toFixed(2));
    return { baseUnitPrice, nfcSurcharge, unitPrice, lineTotal };
  }

  // 3. For existing/legacy orders where unitPrice wasn't stored on the item:
  // Subtract all NFC charges and explicit priceOverrides from order.subtotal first so that
  // the base magnet price is solved cleanly and NFC surcharges remain strictly on NFC items.
  const magnets = order.magnets || [];
  let totalNfcCharges = 0;
  let totalOverrideBase = 0;
  let standardMagnetQty = 0;

  for (const m of magnets) {
    const mQty = Math.max(1, Number(m.quantity) || 1);
    if (m.nfcEnabled) {
      totalNfcCharges += mQty * NFC_SURCHARGE_PER_MAGNET;
    }
    if (m.priceOverride !== undefined && m.priceOverride !== null && Number(m.priceOverride) > 0) {
      totalOverrideBase += mQty * Number(m.priceOverride);
    } else {
      standardMagnetQty += mQty;
    }
  }

  const remainingBaseSubtotal = Math.max(0, (Number(order.subtotal) || 0) - totalNfcCharges - totalOverrideBase);
  const solvedBaseUnitPrice = standardMagnetQty > 0
    ? Number((remainingBaseSubtotal / standardMagnetQty).toFixed(2))
    : 0;

  const unitPrice = Number((solvedBaseUnitPrice + nfcSurcharge).toFixed(2));
  const lineTotal = Number((unitPrice * qty).toFixed(2));

  return {
    baseUnitPrice: solvedBaseUnitPrice,
    nfcSurcharge,
    unitPrice,
    lineTotal,
  };
}

/**
 * Generates and downloads a beautifully styled, high-fidelity PDF invoice for an order.
 * Follows a clean, grid-aligned, professional layout with branding and transaction details.
 */
export async function generateInvoicePDF(order: Order): Promise<void> {
  // Prevent generating invoices for unpaid, cancelled, or failed payment orders
  if (!order || order.status === 'pending' || order.status === 'cancelled' || (order.status as any) === 'failed') {
    console.warn(`Cannot generate paid invoice for order ${order?.id} with status "${order?.status}". Payment was not completed.`);
    return;
  }

  const { jsPDF } = await import('jspdf');

  // Create instance of jsPDF (A4, portrait, millimeters)
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const isRefunded = order.status === 'refunded';

  // Color Palette Constants
  const brandTeal = [0, 128, 128]; // #008080
  const roseRed = [225, 29, 72]; // #e11d48
  const bannerColor = isRefunded ? roseRed : brandTeal;
  const darkSlate = [15, 23, 42]; // #0f172a
  const mutedSlate = [100, 116, 139]; // #64748b
  const lightSlate = [241, 245, 249]; // #f1f5f9
  const borderGray = [226, 232, 240]; // #e2e8f0

  // --- 1. BRAND HEADER BLOCK (White Background with subtle border and Logo Image) ---
  doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
  doc.setLineWidth(0.4);
  doc.rect(15, 15, 180, 20, 'D');

  let logoWidth = 55;
  let logoHeight = 16;
  let imageLoaded = false;
  let imgElement: HTMLImageElement | null = null;
  let logoDataUrl = '';

  // Helper to load image
  const loadImage = (src: string, useCors: boolean): Promise<HTMLImageElement | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      if (useCors) {
        img.crossOrigin = 'anonymous';
      }
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
  };

  try {
    // 1. Try local route first to completely bypass cross-origin/CORS issues
    imgElement = await loadImage('/squaremagnets-logo.webp', false);
    
    // 2. Fallback to absolute remote URL if local loading fails
    if (!imgElement) {
      imgElement = await loadImage('https://squaremagnets.ca/squaremagnets-logo.webp', true);
    }
  } catch (err) {
    console.error('Failed to load company logo image', err);
  }

  if (imgElement) {
    const aspect = imgElement.width / imgElement.height;
    // Cap height at 14mm, calculate width
    logoHeight = 14;
    logoWidth = logoHeight * aspect;
    // Cap width at 75mm to prevent overlapping other header text
    if (logoWidth > 75) {
      logoWidth = 75;
      logoHeight = logoWidth / aspect;
    }

    try {
      // Draw to a canvas and export as base64 PNG to ensure perfect cross-browser compatibility within jsPDF
      const canvas = document.createElement('canvas');
      canvas.width = imgElement.width;
      canvas.height = imgElement.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(imgElement, 0, 0);
        logoDataUrl = canvas.toDataURL('image/png');
        imageLoaded = !!logoDataUrl;
      }
    } catch (canvasErr) {
      console.error('Failed to convert logo image to base64 data URL', canvasErr);
      // Fallback: use imgElement directly if canvas conversion failed due to a tainted canvas
      imageLoaded = true;
    }
  }

  if (imageLoaded && (logoDataUrl || imgElement)) {
    // Center vertically in the 20mm header box (y = 15 + (20 - logoHeight)/2)
    const logoY = 15 + (20 - logoHeight) / 2;
    
    try {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, 'PNG', 18, logoY, logoWidth, logoHeight);
      } else if (imgElement) {
        doc.addImage(imgElement, 'WEBP', 18, logoY, logoWidth, logoHeight);
      }
    } catch (pdfAddErr) {
      console.error('Error adding logo image to PDF doc', pdfAddErr);
      imageLoaded = false; // Trigger text fallback if PDF render failed
    }
  }

  if (imageLoaded) {
    // Position contact details on the right side of the header box
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.text('Square Magnet Co.', 135, 21);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
    doc.text('2564 Sandalwood Drive', 135, 25);
    doc.text('Kamloops, BC, Canada', 135, 29);
  } else {
    // Elegant fallback if image failed to load (offline or CORS issues)
    // Draw modern vector logo for Square Magnet Co.
    doc.setFillColor(brandTeal[0], brandTeal[1], brandTeal[2]);
    doc.rect(19, 19, 12, 12, 'F'); // Centered vertically inside 20mm box: 15 + (20-12)/2 = 19
    
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.5);
    doc.rect(21, 21, 8, 8, 'D');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('S', 23.8, 26.8);

    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('SQUARE MAGNETS', 34, 23);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
    doc.text('Premium Handcrafted Custom Magnets', 34, 28);

    // Position contact details on the right side of the header box
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.text('Square Magnet Co.', 135, 21);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
    doc.text('2564 Sandalwood Drive', 135, 25);
    doc.text('Kamloops, BC, Canada', 135, 29);
  }

  // --- 1.5 SECONDARY BANNER (Full width banner directly below the header box) ---
  doc.setFillColor(bannerColor[0], bannerColor[1], bannerColor[2]);
  doc.rect(15, 37, 180, 10, 'F');

  // PAID/REFUNDED INVOICE title
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text(isRefunded ? 'REFUNDED INVOICE / CREDIT' : 'PAID INVOICE', 19, 43.5);

  // Receipt & Ledger Copy (middle)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text(isRefunded ? 'Refunded & Ledger Copy' : 'Receipt & Ledger Copy', 85, 43.5);

  // Support Email (right)
  doc.text('Support: hello@squaremagnets.ca', 142, 43.5);

  // --- 2. BILL TO & INVOICE METADATA (Two Columns) ---
  let y = 54;

  // Bill To & Ship To Address (Column 1)
  const isDifferentBilling = order.billingSameAsShipping === false && !!(order.billingAddress || order.billingName);

  doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text(isDifferentBilling ? 'BILL TO & SHIP TO ADDRESSES:' : 'CUSTOMER & DELIVERY ADDRESS:', 15, y);

  let addrY = y + 5;
  if (isDifferentBilling) {
    // Billing Details
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(`Billed To: ${order.billingName || order.customerName}`, 15, addrY);
    addrY += 3.8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (order.billingAddress) {
      doc.text(order.billingAddress, 15, addrY);
      addrY += 3.5;
      const bCityZip = [order.billingCity, order.billingZip || order.billingPostalCode, order.billingCountry || 'Canada'].filter(Boolean).join(', ');
      doc.text(bCityZip, 15, addrY);
      addrY += 4;
    }
    // Shipping Details
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(`Ship To: ${order.customerName}`, 15, addrY);
    addrY += 3.8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (order.shippingAddress) {
      doc.text(order.shippingAddress, 15, addrY);
      addrY += 3.5;
      const sCityZip = [order.shippingCity, order.shippingZip || order.shippingPostalCode, order.shippingCountry || 'Canada'].filter(Boolean).join(', ');
      doc.text(sCityZip, 15, addrY);
      addrY += 3.8;
    }
    let contactLine = order.customerEmail || '';
    if (order.customerPhone) {
      contactLine += contactLine ? ` • ${order.customerPhone}` : order.customerPhone;
    }
    if (contactLine) {
      doc.text(contactLine, 15, addrY);
    }
  } else {
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text(order.customerName || 'Valued Customer', 15, addrY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    addrY += 4.5;
    let contactLine = order.customerEmail || '';
    if (order.customerPhone) {
      contactLine += contactLine ? ` • Phone: ${order.customerPhone}` : `Phone: ${order.customerPhone}`;
    }
    doc.text(contactLine || 'N/A', 15, addrY);

    if (order.shippingAddress) {
      addrY += 4.5;
      doc.setFont('helvetica', 'bold');
      doc.text(order.shippingAddress, 15, addrY);
      addrY += 4;
      doc.setFont('helvetica', 'normal');
      const cityZip = [order.shippingCity, order.shippingZip || order.shippingPostalCode].filter(Boolean).join(', ');
      doc.text(cityZip, 15, addrY);
      addrY += 4;
      doc.text(order.shippingCountry || 'Canada', 15, addrY);
    } else if (order.shippingCity) {
      addrY += 4.5;
      doc.setFont('helvetica', 'normal');
      doc.text(`Destination: ${order.shippingCity}`, 15, addrY);
    }
  }

  // Invoice details (Column 2)
  const metaX = 115;
  doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('INVOICE DETAILS:', metaX, y);

  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Order Number:', metaX, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.text(order.id, metaX + 30, y + 5);

  doc.setFont('helvetica', 'bold');
  doc.text('Order Date:', metaX, y + 10);
  doc.setFont('helvetica', 'normal');
  const formattedDate = new Date(order.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.text(formattedDate, metaX + 30, y + 10);

  doc.setFont('helvetica', 'bold');
  doc.text('Paid Via:', metaX, y + 15);
  doc.setFont('helvetica', 'normal');
  const refText = order.paymentId === 'admin_added' 
    ? 'Admin Quick-Add' 
    : (order.paymentId ? 'Stripe' : 'Simulation');
  doc.text(refText, metaX + 30, y + 15);

  doc.setFont('helvetica', 'bold');
  doc.text('Status:', metaX, y + 20);
  if (isRefunded) {
    doc.setTextColor(roseRed[0], roseRed[1], roseRed[2]);
    doc.text('REFUNDED', metaX + 30, y + 20);
  } else {
    doc.setTextColor(16, 185, 129); // emerald-500
    doc.text('PAID', metaX + 30, y + 20);
  }

  // Condensed Gift Order Status in Metadata
  const hasGift = order.isGift === true || 
                  String(order.isGift).toLowerCase() === 'yes' || 
                  (order.isGift as any) === 1 || 
                  !!(order.giftTo || order.giftFrom || order.giftMessage);

  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.setFont('helvetica', 'bold');
  doc.text('Gift Order:', metaX, y + 25);
  doc.setFont('helvetica', 'normal');
  doc.text(hasGift ? 'Yes' : 'No', metaX + 30, y + 25);

  // Dynamic calculation for Column 1 height (with gift information)
  let leftY = Math.max(y + 28, addrY + 4);
  if (hasGift) {
    leftY = y + 15;
    
    const boxX = 15;
    const boxY = leftY;
    const boxW = 85;
    
    // Dynamically calculate box height based on active elements
    let contentHeight = 5; // Title space
    if (order.giftTo) contentHeight += 4.5;
    if (order.giftFrom) contentHeight += 4.5;
    if (order.giftMessage) {
      const wrappedMessage = doc.splitTextToSize(order.giftMessage, boxW - 8);
      contentHeight += 4 + (wrappedMessage.length * 4);
    } else {
      contentHeight += 4.5;
    }
    const boxH = contentHeight + 4; // Padding/margin
    
    // Draw rounded-like box for gift details with a beautiful light rose style
    doc.setFillColor(254, 244, 245); // Soft blush rose-50
    doc.setDrawColor(251, 207, 213); // Soft border rose-200
    doc.setLineWidth(0.3);
    doc.rect(boxX, boxY, boxW, boxH, 'FD');
    
    // Render text inside the box (strictly ASCII characters only to prevent encoding gibberish like Ø<ß•)
    let textY = boxY + 5;
    doc.setTextColor(219, 39, 119); // rose-600
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('GIFT ORDER RECIPIENT (CONDENSED)', boxX + 4, textY);
    textY += 4.5;

    doc.setFontSize(8);
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);

    if (order.giftTo) {
      doc.setFont('helvetica', 'bold');
      doc.text('To:', boxX + 4, textY);
      doc.setFont('helvetica', 'normal');
      doc.text(order.giftTo, boxX + 11, textY);
      textY += 4.5;
    }
    if (order.giftFrom) {
      doc.setFont('helvetica', 'bold');
      doc.text('From:', boxX + 4, textY);
      doc.setFont('helvetica', 'normal');
      doc.text(order.giftFrom, boxX + 14, textY);
      textY += 4.5;
    }
    if (order.giftMessage) {
      doc.setFont('helvetica', 'bold');
      doc.text('Gift Message:', boxX + 4, textY);
      doc.setFont('helvetica', 'normal');
      const wrappedMessage = doc.splitTextToSize(order.giftMessage, boxW - 8);
      doc.text(wrappedMessage, boxX + 4, textY + 3.5);
      textY += 3.5 + (wrappedMessage.length * 4);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
      doc.text('(No custom gift message)', boxX + 4, textY);
    }
    
    leftY = boxY + boxH;
  }

  // Render Fundraiser Campaign box on Invoice if present
  if (order.fundraiserStudentName) {
    const boxX = 15;
    const boxY = leftY + 3;
    const boxW = 85;
    const boxH = 22;
    
    doc.setFillColor(254, 251, 235); // Soft amber-50
    doc.setDrawColor(245, 158, 11); // Soft amber-500 gold border
    doc.setLineWidth(0.3);
    doc.rect(boxX, boxY, boxW, boxH, 'FD');
    
    let textY = boxY + 5;
    doc.setTextColor(180, 83, 9); // amber-700/800
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('SCHOOL / CHARITY FUNDRAISER', boxX + 4, textY);
    textY += 5;
    
    doc.setFontSize(7.5);
    doc.setTextColor(15, 23, 42); // slate-900
    doc.text('Student Name:', boxX + 4, textY);
    doc.setFont('helvetica', 'bold');
    doc.text(order.fundraiserStudentName, boxX + 24, textY);
    textY += 4.5;
    
    doc.setFont('helvetica', 'normal');
    doc.text('Classroom/Grade:', boxX + 4, textY);
    doc.setFont('helvetica', 'bold');
    doc.text(order.fundraiserClassroom || 'N/A', boxX + 28, textY);
    textY += 4.5;
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139); // slate-500
    doc.setFontSize(7);
    if (order.fundraiserDeliveryMethod === 'school' || !order.fundraiserDeliveryMethod) {
      doc.text('Delivery: Free Bulk Delivery to School Location', boxX + 4, textY);
    } else {
      doc.text('Delivery: Standard Courier Shipping to Home Address', boxX + 4, textY);
    }
    
    leftY = boxY + boxH;
  }

  // Render Special Instructions on Invoice if present
  if (order.specialInstructions) {
    const boxX = 15;
    const boxY = leftY + 3;
    const boxW = 85;
    
    const wrappedInstructions = doc.splitTextToSize(order.specialInstructions, boxW - 8);
    const boxH = 5 + 4 + (wrappedInstructions.length * 4) + 2;
    
    doc.setFillColor(248, 250, 252); // slate-50
    doc.setDrawColor(226, 232, 240); // slate-200
    doc.setLineWidth(0.3);
    doc.rect(boxX, boxY, boxW, boxH, 'FD');
    
    let textY = boxY + 5;
    doc.setTextColor(71, 85, 105); // slate-600
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('SPECIAL INSTRUCTIONS', boxX + 4, textY);
    textY += 4.5;
    
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(15, 23, 42); // slate-900
    doc.text(wrappedInstructions, boxX + 4, textY);
    
    leftY = boxY + boxH;
  }

  // --- 3. ITEMS TABLE HEADER ---
  y = Math.max(88, leftY + 8);
  doc.setFillColor(lightSlate[0], lightSlate[1], lightSlate[2]);
  doc.rect(15, y, 180, 8, 'F');

  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Item Description', 18, y + 5.5);
  doc.text('Qty', 140, y + 5.5);
  doc.text('Unit Price', 155, y + 5.5);
  doc.text('Total', 192, y + 5.5, { align: 'right' });

  // Line Separator
  doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
  doc.setLineWidth(0.3);
  doc.line(15, y + 8, 195, y + 8);

  // --- 4. ITEM ROWS ---
  y += 8;
  const magnets = order.magnets || [];
  const descMaxWidth = 118; // Width in mm from x=18 to x=136 (before Qty column at 140)
  const detailLineHeight = 3.6;

  magnets.forEach((mag, index) => {
    const itemPricing = getOrderItemPricing(order, mag);

    // Prepare full, untruncated detail lines and wrap them cleanly within the description column
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);

    const rawDetailParagraphs: string[] = [];
    rawDetailParagraphs.push(
      `Filter: ${mag.filter || 'None'}${mag.text?.fontFamily ? ` | Font: ${mag.text.fontFamily}` : ''}`
    );

    if (mag.text?.text && mag.text.text.trim()) {
      rawDetailParagraphs.push(`Custom Text: "${mag.text.text.trim()}"`);
    }

    if (mag.additionalTexts && mag.additionalTexts.length > 0) {
      mag.additionalTexts.forEach((t, tIdx) => {
        if (t?.text && t.text.trim()) {
          rawDetailParagraphs.push(`Additional Text #${tIdx + 1}: "${t.text.trim()}"`);
        }
      });
    }

    if (mag.qrCode?.url && mag.qrCode.url.trim()) {
      rawDetailParagraphs.push(`QR Code URL: ${mag.qrCode.url.trim()}`);
    }

    if (mag.nfcEnabled) {
      rawDetailParagraphs.push(
        `NFC Tag (${mag.nfcOption || 'Programmed'}): Base $${itemPricing.baseUnitPrice.toFixed(2)} + NFC $${itemPricing.nfcSurcharge.toFixed(2)}/ea`
      );
      rawDetailParagraphs.push(
        `NFC URL / Data: ${mag.nfcData ? mag.nfcData.trim() : 'No data provided'}`
      );
    }

    const wrappedDetailLines: string[] = [];
    for (const paragraph of rawDetailParagraphs) {
      const splitLines = doc.splitTextToSize(paragraph, descMaxWidth) as string[];
      wrappedDetailLines.push(...splitLines);
    }

    const rowHeight = Math.max(12, 7.5 + wrappedDetailLines.length * detailLineHeight + 2);

    // Add a new page if this item row would overflow the bottom margin
    if (y + rowHeight > 272) {
      doc.addPage();
      y = 20;
    }

    // Alternating background colors for rows
    if (index % 2 === 1) {
      doc.setFillColor(250, 250, 250);
      doc.rect(15, y, 180, rowHeight, 'F');
    }

    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    
    // Description Label
    const itemNum = index + 1;
    let descriptionLabel = `Custom Magnet Design #${itemNum}`;
    if (mag.isMomentsTemplate) {
      descriptionLabel = `Custom Moments Magnet Template #${itemNum}`;
    } else if (mag.isPremade) {
      descriptionLabel = `Curated Magnet Design #${itemNum}`;
    }
    if (mag.nfcEnabled) {
      descriptionLabel += ` (+NFC Smart Tag)`;
    }
    doc.text(descriptionLabel, 18, y + 5.5);

    // Render all wrapped detail lines in full (no truncation)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
    let detailY = y + 9.5;
    for (const line of wrappedDetailLines) {
      if (line.startsWith('NFC Tag') || line.startsWith('NFC URL / Data:')) {
        doc.setTextColor(brandTeal[0], brandTeal[1], brandTeal[2]);
        doc.setFont('helvetica', 'bold');
      } else {
        doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
        doc.setFont('helvetica', 'normal');
      }
      doc.text(line, 18, detailY);
      detailY += detailLineHeight;
    }

    // Quantity column
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.text(String(mag.quantity), 140, y + 6);

    // Unit Price column (individual price preserved intact, never averaged)
    doc.text(`$${itemPricing.unitPrice.toFixed(2)}`, 155, y + 6);

    // Line Total column
    doc.setFont('helvetica', 'bold');
    doc.text(`$${itemPricing.lineTotal.toFixed(2)}`, 192, y + 6, { align: 'right' });

    // Draw horizontal separator
    doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
    doc.line(15, y + rowHeight, 195, y + rowHeight);

    y += rowHeight;
  });

  // Ensure enough space on page for totals and footer block
  if (y + 65 > 282) {
    doc.addPage();
    y = 20;
  }

  // --- 5. SUB-TOTAL & BALANCE DETAILS ---
  y += 4;
  const summaryXLabel = 135;
  const summaryXVal = 192;

  doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  // Subtotal Row
  doc.text('Subtotal:', summaryXLabel, y);
  doc.text(`$${order.subtotal.toFixed(2)}`, summaryXVal, y, { align: 'right' });

  // Optional Discount Row
  if (order.discountAmount && order.discountAmount > 0) {
    y += 5;
    doc.setTextColor(4, 120, 87);
    doc.text(`Discount${order.couponCode ? ` (${order.couponCode})` : ''}:`, summaryXLabel, y);
    doc.text(`-$${order.discountAmount.toFixed(2)}`, summaryXVal, y, { align: 'right' });
    doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
  }

  // Shipping Cost Row
  y += 5;
  doc.text('Shipping:', summaryXLabel, y);
  const shipText = `$${order.shippingCost.toFixed(2)}`;
  doc.text(shipText, summaryXVal, y, { align: 'right' });

  // Total Row
  y += 7;
  doc.setFillColor(lightSlate[0], lightSlate[1], lightSlate[2]);
  doc.rect(summaryXLabel - 5, y - 4, 65, 8.5, 'F');
  
  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('TOTAL PAID:', summaryXLabel, y + 1.5);
  doc.text(`$${order.total.toFixed(2)} CAD`, summaryXVal, y + 1.5, { align: 'right' });

  // Payment Status Watermark Banner
  y += 18;
  if (isRefunded) {
    doc.setDrawColor(roseRed[0], roseRed[1], roseRed[2]);
    doc.setFillColor(255, 241, 242); // rose-50
    doc.rect(15, y, 180, 10, 'FD');

    doc.setTextColor(190, 18, 60); // rose-700
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text('PAYMENT STATUS: REFUNDED & SETTLED', 60, y + 6.5);
  } else {
    doc.setDrawColor(16, 185, 129); // emerald-500
    doc.setFillColor(240, 253, 250); // emerald-50
    doc.rect(15, y, 180, 10, 'FD');

    doc.setTextColor(4, 120, 87); // emerald-700
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text('PAYMENT STATUS: COMPLETED & FULLY SETTLED — THANK YOU', 45, y + 6.5);
  }

  // --- 6. PROFESSIONAL TERMS & CUSTOMER POLICIES ---
  y += 20;
  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text('Fulfillment Policy & Information:', 15, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
  doc.text('- Processing Time: Every order undergoes a prepress verification, custom printing, and hand-pressing.', 15, y + 4);
  doc.text('- Need Assistance? Connect directly with support by emailing hello@squaremagnets.ca - We are happy to help!', 15, y + 7.5);

  // Divider
  doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
  doc.line(15, y + 12, 195, y + 12);

  // --- 7. FOOTER NOTE ---
  doc.setTextColor(mutedSlate[0], mutedSlate[1], mutedSlate[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('Thank you for supporting a British Columbia small business!', 65, y + 18);

  // Trigger browser download dialog
  doc.save(`${isRefunded ? 'Refunded' : 'Paid'}_Invoice_SquareMagnets_${order.id}.pdf`);
}
